import type { Daypart } from './daypart';

export type Rng = () => number;

export type RoamMove = 'rest' | 'stroll' | 'hop' | 'climb';

export interface DwellRange {
  meanMs: number;
  minMs: number;
  maxMs: number;
}

export const PAUSE_DWELL: DwellRange = { meanMs: 4200, minMs: 1500, maxMs: 13000 };

/** How long the companion must stay idle before it stretches and falls
 *  asleep. Ambient tuning, not physics — adjust freely. */
export const LONG_IDLE_MS = 45_000;

/** How much longer a rest beat lasts in each part of day. Night reads as
 *  winding down; morning is a touch brisker than baseline. Ambient tuning,
 *  not physics. */
export const DAYPART_DWELL_SCALE: Record<Daypart, number> = {
  morning: 0.9,
  day: 1,
  evening: 1.3,
  night: 1.8,
};

/** A pause length for the given part of day. The clamp still applies after
 *  scaling, so a long night rest can reach the range ceiling but a short one
 *  never dips under the floor. */
export function pauseDwellMs(daypart: Daypart | undefined, rng: Rng = Math.random): number {
  const base = dwellMs(PAUSE_DWELL, rng);
  if (!daypart) return base;
  return Math.min(PAUSE_DWELL.maxMs, Math.max(PAUSE_DWELL.minMs, base * DAYPART_DWELL_SCALE[daypart]));
}

const REST_CHANCE = 0.62;
const CLIMB_CHANCE = 0.12;
const HOP_CHANCE = 0.2;
const STROLL_MIN_FRACTION = 0.45;
const STROLL_MIN_PX = 110;
const STROLL_TOWARD_ROOM = 0.85;
/** Resting while home is reachable but unclaimed — lower than REST_CHANCE so
 *  the companion actually goes, rather than dithering on the floor beneath it. */
const HOMEWARD_REST_CHANCE = 0.35;
/** Hopping, once home is one of the reachable ledges. */
const HOMEWARD_HOP_CHANCE = 0.75;
/** Resting while already perched. High, so the companion settles instead of
 *  bouncing on and off, but short of certainty so it still wanders off. */
const PERCHED_REST_CHANCE = 0.88;

/**
 * Exponential (memoryless) dwell, clamped. Uniform pauses read as metronomic;
 * an exponential gives mostly short rests with a rare long loaf, so the rhythm
 * never exposes a fixed timer.
 */
export function dwellMs(
  { meanMs, minMs, maxMs }: DwellRange,
  rng: Rng = Math.random,
): number {
  const u = 1 - rng();
  return Math.min(maxMs, Math.max(minMs, -Math.log(u) * meanMs));
}

export interface MoveOptions {
  canHop: boolean;
  canClimb: boolean;
  /** The favoured perch is one of the ledges within hopping reach. */
  favouredReachable?: boolean;
  /** The companion is already standing on the favoured perch. */
  onFavoured?: boolean;
}

export function chooseMove(
  { canHop, canClimb, favouredReachable, onFavoured }: MoveOptions,
  rng: Rng = Math.random,
): RoamMove {
  // Resting dominates every other choice, so a pull toward the favoured perch
  // has to act here or it never gets a turn: once perched the companion
  // settles, and while home is in reach it dithers less before going.
  const rest = onFavoured ? PERCHED_REST_CHANCE
    : favouredReachable ? HOMEWARD_REST_CHANCE
      : REST_CHANCE;
  if (rng() < rest) return 'rest';
  if (canClimb && rng() < CLIMB_CHANCE) return 'climb';
  if (canHop && rng() < (favouredReachable ? HOMEWARD_HOP_CHANCE : HOP_CHANCE)) return 'hop';
  return 'stroll';
}

/**
 * A stroll destination on the surface that actually goes somewhere: lean toward
 * the side with more room and guarantee a real minimum travel, so the companion
 * crosses the workspace instead of shuffling in one spot.
 */
export function pickStrollTarget(
  { left, right }: { left: number; right: number },
  fromX: number,
  rng: Rng = Math.random,
): number {
  const span = right - left;
  if (span <= 4) return left;

  const roomLeft = fromX - left;
  const roomRight = right - fromX;
  const goRight = (rng() < STROLL_TOWARD_ROOM) === (roomRight >= roomLeft);
  const room = Math.max(0, goRight ? roomRight : roomLeft);
  const minDist = Math.min(room, Math.max(span * STROLL_MIN_FRACTION, STROLL_MIN_PX));
  const dist = minDist + rng() * Math.max(0, room - minDist);

  return goRight ? fromX + dist : fromX - dist;
}
